// The inline sentence editor's PURE half — everything the expanding row panel
// (`components/alarms/redesign/InlineAlarmEditor.tsx`) decides before it renders
// anything.
//
// The editor itself is deliberately thin: it owns no vocabulary and no
// arithmetic. Every word it prints comes from the layer that already ships —
// `customAlarmForm` / `alarmDefsForm` for the drafts, `alarmSentence` for the
// phrasing, `watchSchedule` for the presets, `alarmDisplay` for severities and
// delivery — and this module is the glue that says which of those a given
// definition gets, in which order, and what the save is allowed to claim.
//
// Three decisions live here, and all three are the kind that go wrong silently:
//
//   1. WHICH VARIANT. A custom definition edits through the custom draft and a
//      recipe definition through the recipe draft, and the two must never
//      cross: a `custom:` def handed the recipe editor would be saved through a
//      template it has no relationship with. `isCustomDefinition` is the one
//      test (it catches both a readable custom block and a bare `custom:` id).
//
//   2. HOW MANY BOXES A RECIPE PARAM GETS. A param carrying ONE shared number
//      gets one box. A param the operator broke out per mode (`isSplitByMode`,
//      which `initFromDef` derives from stored per-mode divergence) gets ONE BOX
//      PER ENABLED MODE — never a single box over per-mode values, which would
//      show the first mode's number and save it over every other mode. The split
//      itself is the operator's to change either way, and collapsing it back
//      throws numbers away, so the model carries the warning that says WHICH
//      ones before the click rather than after it. Never flatten silently.
//
//   3. WHAT THE SENTENCE SAYS. The segment list is data, not markup, so the
//      component maps over it and the words are unit-tested here. The verbs are
//      `alarmSentence`'s own ("goes above", "drops below", "turns on", "stops
//      reporting for"), because the editor must read as the same sentence the
//      row above it reads. An UNKNOWN trigger class — `EntryOn` and
//      `AnchorWatch` are compiled wire classes with no custom-authoring form,
//      and a newer build may invent more — degrades to the neutral subject with
//      NO number slot and NO duration slot. Inventing an editable threshold for
//      a class this build cannot author is how an operator saves a number the
//      compiler never asked for.
//
// No React, no JSX, no DOM: the tests are plain vitest over data.

import type { VesselType } from '@neomaris/protocol';
import { isWithdrawnRecipe, withdrawnRecipeMessage } from '@neomaris/rules';

import {
  bindingChoices,
  canSplitByMode,
  isSplitByMode,
  likelyInstanceHint,
  modeValue,
  pinCopyFor,
  sharedValue,
  splitCollapseLoses,
  type AlarmDraft,
  type BindingProblem,
} from './alarmDefsForm';
import {
  ALARM_SEVERITIES,
  alarmSeverityLabel,
  describeDelivery,
  isCustomDefinition,
  readBindingPath,
  resolvedPathFor,
  type AlarmDefView,
  type AlarmSeverity,
  type DeliveryState,
} from './alarmDisplay';
import { paramEditorUnits, pathFamilyMatcher } from './alertTemplates';
import { isRecipeWithdrawnError, withdrawnErrorMessage } from './customAlarmErrors';
import { apiErrorCode } from './marineToolkit';
import { CUSTOM_TIMING_LABELS, type CustomAlarmDraft } from './customAlarmForm';
import type { Signal } from './signalCatalog';
import { detectWatchPreset, watchPresets, CUSTOM_WATCH_LABEL } from './watchSchedule';
import type { WatchPresetId } from './watchSchedule';

// ---------------------------------------------------------------------------
// Which editor a definition gets
// ---------------------------------------------------------------------------

/**
 * Which arm of the inline editor a stored definition belongs to. `withdrawn` is
 * a recipe alarm whose recipe is WITHDRAWN (core ADR-216, TOM-136): nothing can
 * arm it and the server refuses an edit that keeps it on, so it gets no form —
 * only the reason, Turn off and Delete.
 */
export type InlineEditorVariant = 'custom' | 'recipe' | 'withdrawn';

/**
 * The variant for one definition. A custom def gets the custom arm even when
 * this build cannot read its block back into a draft — the panel then says so
 * and links to the full editor, rather than falling through to a recipe form
 * that would save it as something else.
 */
export function inlineEditorVariant(def: {
  readonly recipeId?: string;
  readonly custom?: unknown;
}): InlineEditorVariant {
  if (isCustomDefinition(def)) return 'custom';
  return def.recipeId !== undefined && isWithdrawnRecipe(def.recipeId) ? 'withdrawn' : 'recipe';
}

/**
 * What an alarm on a WITHDRAWN recipe says in place of its editor (TOM-136):
 * the registry's own sentence, then the two things the owner can still do.
 * Shared by the inline panel and the full editor so they say one thing.
 */
export function withdrawnAlarmNote(recipeId: string, enabled: boolean): string {
  const next = enabled ? 'You can turn it off or delete it.' : 'You can delete it.';
  return `${withdrawnRecipeMessage(recipeId)} ${next}`;
}

/** The full editor for one alarm — the panel's escape hatch, one place. */
export function fullEditorPath(boatId: string, alarmId: string): string {
  return `/dashboard/boats/${boatId}/alarms/${alarmId}`;
}

// ---------------------------------------------------------------------------
// Severity and watch-schedule slot vocabularies
// ---------------------------------------------------------------------------

/** One dropdown slot choice: what the wire carries, and what the chip reads. */
export interface SlotOption<V extends string = string> {
  readonly value: V;
  readonly label: string;
}

/**
 * The severity slot's choices, from the shared vocabulary in shared order.
 * Never a hand-written list — one dropped entry downgrades an alarm without
 * saying so (the same trap `customAlarmForm`'s `SEVERITIES` set records).
 */
export function severityOptions(): SlotOption<AlarmSeverity>[] {
  return ALARM_SEVERITIES.map((value) => ({ value, label: alarmSeverityLabel(value) }));
}

/** "a WARNING" / "an EMERGENCY" — the article the severity word takes. */
export function severityArticle(severity: AlarmSeverity): 'a' | 'an' {
  return /^[aeiou]/i.test(alarmSeverityLabel(severity)) ? 'an' : 'a';
}

/**
 * The watch slot's choices and which one is selected.
 *
 * A stored mode set that matches no preset reads as `custom`, and that entry is
 * offered as an INERT selected option: opening the editor must never rewrite a
 * stored set (parity §12e — unusual sets show what they have), so "Custom…" is
 * selectable-looking but writes nothing. Picking a real preset writes exactly
 * that preset's modes through `setEnabledModes`.
 */
export function watchSlotOptions(
  enabledModes: readonly string[],
  vesselType?: VesselType | null
): { readonly options: SlotOption<WatchPresetId>[]; readonly selected: WatchPresetId } {
  const selected = detectWatchPreset(enabledModes);
  const options: SlotOption<WatchPresetId>[] = watchPresets(vesselType).map((preset) => ({
    value: preset.id,
    label: preset.label,
  }));
  if (selected === 'custom') options.push({ value: 'custom', label: CUSTOM_WATCH_LABEL });
  return { options, selected };
}

/** True for the one watch choice that must not write anything when picked. */
export function isInertWatchPreset(id: WatchPresetId): boolean {
  return id === 'custom';
}

// ---------------------------------------------------------------------------
// Recipe params: one shared box, or one box per mode
// ---------------------------------------------------------------------------

/** One editable number box: a mode's own value, or the one shared value. */
export interface InlineParamField {
  /** The mode this box writes, or `null` for the shared box (fans out). */
  readonly mode: string | null;
  /** What the box is captioned with — the mode name, on a split param. */
  readonly caption: string;
  /** The draft's raw string, already in the box's unit — never converted here. */
  readonly value: string;
  /** What a screen reader hears: the setting's name, and which mode it is for. */
  readonly srLabel: string;
}

/**
 * One recipe param as this panel edits it — the boxes it gets, and whether the
 * operator may break it out per mode.
 *
 * The draft already holds values in the unit the box shows (`initFromDef` runs
 * them through `paramValueText`), so nothing here converts — it only carries
 * the suffix and the bounds `paramEditorUnits` resolved for that unit. The
 * writes go back through `setThreshold`, which converts at the same edge.
 */
export interface InlineRecipeParam {
  readonly key: string;
  readonly label: string;
  /** What follows the number, in the unit the BOX shows (`paramEditorUnits`). */
  readonly unitSuffix: string;
  readonly step?: number;
  readonly min?: number;
  readonly max?: number;
  /** True while this param carries a different number per mode. */
  readonly split: boolean;
  /** True when the RECIPE allows the split — whether the toggle exists at all. */
  readonly canSplit: boolean;
  /**
   * The boxes, in reading order: one per enabled mode while `split`, otherwise
   * the single shared one. A split param with no enabled mode has none — the
   * draft's own "choose at least one mode" error is what says so.
   */
  readonly fields: readonly InlineParamField[];
  /**
   * What collapsing this param back to one number would DISCARD, worded — or
   * `null` when nothing would be lost. A standing line beside the toggle, said
   * before the click: the box the operator would have to re-type is gone after.
   */
  readonly collapseWarning: string | null;
}

/** The toggle beside a param that may vary per mode — the full editor's words. */
export const SPLIT_TOGGLE_LABEL = 'Set a different value for each mode';

/** The screen-reader name for one mode's box on a split param. */
function paramFieldLabel(label: string, mode: string | null): string {
  return mode === null ? label : `${label} — ${mode}`;
}

/**
 * The warning `AlarmDefinitionForm` prints beside the same toggle, word for
 * word: which number survives the collapse and which ones go.
 */
function collapseWarningText(draft: AlarmDraft, key: string, unitSuffix: string): string | null {
  const lost = splitCollapseLoses(draft, key);
  if (lost === null) return null;
  const keep = sharedValue(draft, key);
  const suffix = unitSuffix.length > 0 ? ` ${unitSuffix}` : '';
  return `Turning this off keeps ${keep}${suffix} for every mode and drops ${lost.join(', ')}.`;
}

/** Every recipe param this panel renders, in the recipe's own order. */
export function recipeParamSlots(draft: AlarmDraft): InlineRecipeParam[] {
  return draft.params.map((param) => {
    // From the DRAFT's units, which is the whole reason they ride on it: the
    // boxes were FILLED by `initFromDef` in these units, so the chip beside the
    // number, the bounds and the step have to be resolved from the same ones. A
    // draft assembled by hand carries none and every box stays in store units,
    // which is what this panel did before ADR 0189.
    const units = paramEditorUnits(param, draft.units);
    const split = isSplitByMode(draft, param.key);
    const fields: InlineParamField[] = split
      ? draft.enabledModes.map((mode) => ({
          mode,
          caption: mode,
          value: modeValue(draft, mode, param.key),
          srLabel: paramFieldLabel(param.label, mode),
        }))
      : [
          {
            mode: null,
            caption: param.label,
            value: sharedValue(draft, param.key),
            srLabel: paramFieldLabel(param.label, null),
          },
        ];
    return {
      key: param.key,
      label: param.label,
      unitSuffix: units.unitSuffix,
      ...(units.step !== undefined ? { step: units.step } : {}),
      ...(units.min !== undefined ? { min: units.min } : {}),
      ...(units.max !== undefined ? { max: units.max } : {}),
      split,
      canSplit: canSplitByMode(draft, param.key),
      fields,
      collapseWarning: split ? collapseWarningText(draft, param.key, units.unitSuffix) : null,
    };
  });
}

/**
 * The mode a shared box writes THROUGH. `setThreshold` fans a shared param out
 * to every mode itself, but it still takes a mode argument, and handing it one
 * the draft does not carry would write nothing on a param that IS split.
 */
export function writeModeFor(draft: AlarmDraft, field: InlineParamField): string {
  return field.mode ?? draft.enabledModes[0] ?? draft.modeUniverse[0] ?? '';
}

// ---------------------------------------------------------------------------
// The instance slot — which one of several this alarm watches
// ---------------------------------------------------------------------------

/** One optgroup in the instance slot. An EMPTY label means "no optgroup". */
export interface BindingSlotGroup {
  readonly label: string;
  readonly options: readonly SlotOption[];
}

/** The instance dropdown, or `null` when there is no question worth asking. */
export interface BindingSlot {
  /** The stored pin, `''` for "any" — what the select shows as chosen. */
  readonly selected: string;
  /** The question, as the select's accessible name. */
  readonly label: string;
  /**
   * The first entry, always value `''`. On an ordinary recipe it is the real
   * choice "Automatic"; on a pin-mandatory one it is a REFUSABLE placeholder —
   * `validate` rejects the empty pin, so picking it can never be saved.
   */
  readonly placeholder: SlotOption;
  /** True when this recipe refuses to run unpinned (core ADR-116 part A). */
  readonly required: boolean;
  readonly groups: readonly BindingSlotGroup[];
  /** The stored pin the compiler cannot bind, worded — or `null`. */
  readonly problem: string | null;
}

/** The question over the select — the full editor's words, both variants. */
export const BINDING_QUESTION = 'Which one should it watch?';
/** `bilge_high`'s pin question; `bilge_pump_cycling` asks for the pump (TOM-101). */
export const BINDING_QUESTION_REQUIRED = pinCopyFor('bilge_high').question;

/** What "no pin" is called: a real choice, or the refusable placeholder. */
export const BINDING_AUTOMATIC_LABEL = 'Automatic (first reported)';
export const BINDING_CHOOSE_LABEL = 'Choose a channel…';

/**
 * A pin the compiler cannot bind, said in the words the full editor uses.
 * Kept per-variant rather than folded: "your boat isn't sending this" and "this
 * isn't something this alarm can watch" call for different fixes, and a
 * pin-mandatory recipe has no Automatic to be sent back to.
 */
export function bindingProblemText(problem: BindingProblem, required: boolean): string {
  if (problem === 'not-reported') {
    return required
      ? 'Your boat isn’t reporting this channel right now, so the alarm is waiting for it rather than watching another. Pick one that is reporting.'
      : 'Your boat isn’t reporting this one right now, so the alarm is waiting for it rather than watching another. Pick one that is reporting, or switch to Automatic.';
  }
  return required
    ? 'This alarm can’t watch the channel it’s set to, so it isn’t running. Pick one from the list.'
    : 'This alarm can’t watch the one it’s set to, so it isn’t running. Pick one from the list, or switch to Automatic.';
}

/**
 * A source whose reading the recipe draft can honestly show. An explicit draft
 * pin is known; clearing a stored pin to Automatic is not: only the compiler
 * can resolve its preferred source. Wait for save/read-back rather than showing
 * the old pin's reading under the new Automatic choice.
 */
export function recipeSourcePath(
  draft: Pick<AlarmDraft, 'binding'> | null,
  baseline: AlarmDefView
): string | null {
  if (draft === null) return resolvedPathFor(baseline);
  const selected = draft.binding.trim();
  if (selected.length > 0) return selected;
  if (readBindingPath(baseline) !== undefined) return null;
  return resolvedPathFor(baseline);
}

/**
 * The source slot for a recipe draft. A one-member family stays selectable so
 * the owner can explicitly pin it. No family metadata or an unavailable catalog
 * returns `null`: this build cannot invent sources the compiler would accept.
 *
 * The catalog is passed in rather than fetched: with an EMPTY list every stored
 * pin would read as "not currently reported", and a failed catalog fetch must
 * not flag a perfectly healthy pin (the same rule the custom arm's validation
 * follows). No slot means the untouched draft carries the stored pin through
 * the save unchanged.
 */
export function bindingSlot(draft: AlarmDraft, signals: readonly Signal[]): BindingSlot | null {
  if (signals.length === 0) return null;
  const required = draft.bindingRequired === true;
  const hint = likelyInstanceHint(draft.recipeId);
  const choices = bindingChoices(signals, pathFamilyMatcher(draft.bindingFamily), draft.binding, {
    required,
    showSingle: true,
    ...(hint !== undefined ? { hint } : {}),
  });
  if (choices.length === 0) return null;
  const likely = choices.filter((c) => c.likely === true);
  const other = choices.filter((c) => c.likely !== true);
  const asOption = (c: (typeof choices)[number]): SlotOption => ({
    value: c.path,
    label: c.label,
  });
  // Both halves, likely FIRST — and the other half is never dropped: every path
  // in the family stays choosable however unpromising its name (ADR 0157, and
  // `bindingChoices`'s own rule). A group with nothing in it is not a hidden
  // half, so it gets no empty optgroup.
  const groups: BindingSlotGroup[] =
    hint !== undefined && likely.length > 0
      ? [
          { label: hint.likelyGroup, options: likely.map(asOption) },
          ...(other.length > 0 ? [{ label: hint.otherGroup, options: other.map(asOption) }] : []),
        ]
      : [{ label: '', options: choices.map(asOption) }];
  const problem = choices.find((c) => c.path === draft.binding)?.problem;
  return {
    selected: draft.binding,
    label: required ? pinCopyFor(draft.recipeId).question : BINDING_QUESTION,
    placeholder: { value: '', label: required ? BINDING_CHOOSE_LABEL : BINDING_AUTOMATIC_LABEL },
    required,
    groups,
    problem: problem === undefined ? null : bindingProblemText(problem, required),
  };
}

// ---------------------------------------------------------------------------
// The sentence, as an ordered list of segments
// ---------------------------------------------------------------------------

/** The custom editor's editable duration fields — all authored in SECONDS. */
export type DurationField = 'setDelaySeconds' | 'clearDelaySeconds' | 'staleSeconds';

/** The custom editor's editable number fields (analog set / clear points). */
export type NumberField = 'setpoint' | 'clearpoint';

/**
 * One piece of the slotted sentence. `text` and `subject` are printed; the rest
 * are the editable slots, each naming the draft field it binds to.
 */
export type SentenceSegment =
  | { readonly kind: 'text'; readonly text: string }
  /** The selected source's name; the source picker owns changing it. */
  | { readonly kind: 'subject'; readonly text: string }
  | { readonly kind: 'severity'; readonly value: AlarmSeverity }
  | {
      readonly kind: 'number';
      readonly field: NumberField;
      readonly value: string;
      /** The signal's store unit, glyph-spelled; `null` when it declares none. */
      readonly unit: string | null;
      /** True for the clear point, which is legitimately blank. */
      readonly optional: boolean;
      /** What a screen reader hears for the box. */
      readonly label: string;
    }
  | {
      readonly kind: 'duration';
      readonly field: DurationField;
      readonly value: string;
      readonly label: string;
    }
  | { readonly kind: 'watch'; readonly preset: WatchPresetId };

/** What follows every duration box. Seconds is the authored unit; no new one. */
export const DURATION_UNIT = 's';

/** Screen-reader label per duration slot — the builder's own words. */
export function durationLabel(field: DurationField): string {
  switch (field) {
    case 'setDelaySeconds':
      return CUSTOM_TIMING_LABELS.setDelay;
    case 'clearDelaySeconds':
      return CUSTOM_TIMING_LABELS.clearDelay;
    case 'staleSeconds':
      return CUSTOM_TIMING_LABELS.stale;
  }
}

/**
 * Everything the sentence needs, flattened out of the draft.
 *
 * `triggerClass` is a plain string on purpose. The custom draft can only hold
 * the five classes this build authors, but a definition compiled by the boat
 * can carry `EntryOn`, `AnchorWatch`, or something newer — and those reach this
 * function through the same door. Typing it narrowly would have hidden them
 * behind a cast instead of making the degrade a tested path.
 */
export interface SentenceInput {
  readonly triggerClass?: string;
  /** The signal's human name — bold, and never invented ("This signal"). */
  readonly subject: string;
  readonly unit?: string | null;
  readonly severity: AlarmSeverity;
  readonly setpoint?: string;
  readonly clearpoint?: string;
  readonly setDelaySeconds?: string;
  readonly clearDelaySeconds?: string;
  readonly staleSeconds?: string;
  readonly watchPreset: WatchPresetId;
}

/** The sentence input for a custom draft, plus the words only the page knows. */
export function sentenceInputFromDraft(
  draft: CustomAlarmDraft,
  options: {
    readonly subject: string;
    readonly unit?: string | null;
    readonly vesselType?: VesselType | null;
  }
): SentenceInput {
  return {
    ...(draft.triggerClass !== undefined ? { triggerClass: draft.triggerClass } : {}),
    subject: options.subject,
    unit: options.unit ?? null,
    severity: draft.severity,
    ...(draft.setpoint !== undefined ? { setpoint: draft.setpoint } : {}),
    ...(draft.clearpoint !== undefined ? { clearpoint: draft.clearpoint } : {}),
    setDelaySeconds: draft.setDelaySeconds,
    clearDelaySeconds: draft.clearDelaySeconds,
    ...(draft.staleSeconds !== undefined ? { staleSeconds: draft.staleSeconds } : {}),
    watchPreset: detectWatchPreset(draft.enabledModes),
  };
}

/** The verb for a trigger class, or `null` when this build has no words for it. */
function triggerVerb(triggerClass: string | undefined): string | null {
  switch (triggerClass) {
    case 'High':
      return 'goes above';
    case 'Low':
      return 'drops below';
    case 'On':
      return 'turns on';
    case 'Off':
      return 'turns off';
    default:
      return null;
  }
}

/** "clears below" for a High, "clears above" for a Low. */
function clearDirection(triggerClass: string | undefined): string {
  return triggerClass === 'High' ? 'below' : 'above';
}

/**
 * The slotted sentence for a custom alarm, in reading order.
 *
 * Analog (High / Low): severity · subject · verb · set point · set delay ·
 * clear point · clear delay · watch.
 * Binary (On / Off): severity · subject · verb · set delay · watch — no
 * thresholds, no clear clause, exactly as the design draws it.
 * DataUnavailable: severity · subject · "stops reporting for" · stale · watch.
 * Anything else: severity · subject · "meets its trigger" · watch — the
 * `alarmSentence` degrade, with no editable number anywhere.
 */
export function sentenceSegments(input: SentenceInput): SentenceSegment[] {
  const unit = input.unit ?? null;
  const segments: SentenceSegment[] = [
    { kind: 'text', text: `Raise ${severityArticle(input.severity)} ` },
    { kind: 'severity', value: input.severity },
    { kind: 'text', text: ' when ' },
    { kind: 'subject', text: input.subject },
  ];
  const verb = triggerVerb(input.triggerClass);
  const analog = input.triggerClass === 'High' || input.triggerClass === 'Low';

  if (analog && verb !== null) {
    segments.push({ kind: 'text', text: ` ${verb} ` });
    segments.push({
      kind: 'number',
      field: 'setpoint',
      value: input.setpoint ?? '',
      unit,
      optional: false,
      label: 'Set point',
    });
    segments.push({ kind: 'text', text: ' for ' });
    segments.push({
      kind: 'duration',
      field: 'setDelaySeconds',
      value: input.setDelaySeconds ?? '',
      label: durationLabel('setDelaySeconds'),
    });
    // The clear clause is ALWAYS offered on an analog alarm, blank included: a
    // blank clear point means "clears at the set point" (the builder's own
    // hint), and hiding the box when it is blank would leave no way to add
    // hysteresis without opening the full editor.
    segments.push({ kind: 'text', text: `, clears ${clearDirection(input.triggerClass)} ` });
    segments.push({
      kind: 'number',
      field: 'clearpoint',
      value: input.clearpoint ?? '',
      unit,
      optional: true,
      label: 'Clear point',
    });
    segments.push({ kind: 'text', text: ' for ' });
    segments.push({
      kind: 'duration',
      field: 'clearDelaySeconds',
      value: input.clearDelaySeconds ?? '',
      label: durationLabel('clearDelaySeconds'),
    });
  } else if (verb !== null) {
    segments.push({ kind: 'text', text: ` ${verb} for ` });
    segments.push({
      kind: 'duration',
      field: 'setDelaySeconds',
      value: input.setDelaySeconds ?? '',
      label: durationLabel('setDelaySeconds'),
    });
  } else if (input.triggerClass === 'DataUnavailable') {
    segments.push({ kind: 'text', text: ' stops reporting for ' });
    segments.push({
      kind: 'duration',
      field: 'staleSeconds',
      value: input.staleSeconds ?? '',
      label: durationLabel('staleSeconds'),
    });
  } else {
    // `EntryOn`, `AnchorWatch`, a class from a newer build, or a draft with no
    // class at all. Say what `alarmSentence` says and offer nothing to edit.
    segments.push({ kind: 'text', text: ' meets its trigger' });
  }

  segments.push({ kind: 'text', text: ', watching ' });
  segments.push({ kind: 'watch', preset: input.watchPreset });
  return segments;
}

/**
 * The whole sentence as plain text — the panel's accessible name, and what the
 * tests read. A blank slot prints an em dash rather than nothing, so a missing
 * number is audible instead of silently closing the gap.
 */
export function segmentsPlainText(
  segments: readonly SentenceSegment[],
  options?: { readonly watchLabelFor?: (preset: WatchPresetId) => string }
): string {
  const watchLabelFor =
    options?.watchLabelFor ??
    ((preset: WatchPresetId) =>
      watchPresets().find((p) => p.id === preset)?.label ?? CUSTOM_WATCH_LABEL);
  let out = '';
  for (const segment of segments) {
    switch (segment.kind) {
      case 'text':
      case 'subject':
        out += segment.text;
        break;
      case 'severity':
        out += alarmSeverityLabel(segment.value);
        break;
      case 'number': {
        const value = segment.value.trim();
        out += value.length === 0 ? '—' : value;
        if (segment.unit !== null && segment.unit.trim().length > 0) out += ` ${segment.unit}`;
        break;
      }
      case 'duration': {
        const value = segment.value.trim();
        out += `${value.length === 0 ? '—' : value} ${DURATION_UNIT}`;
        break;
      }
      case 'watch':
        out += watchLabelFor(segment.preset);
        break;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// The save flow
// ---------------------------------------------------------------------------

/**
 * Where the save is. `saved` carries the DELIVERY note rather than a success
 * word: "saved" is a cloud fact, and the only thing worth interrupting an
 * operator with is the case where the boat has NOT got the change yet.
 */
export type InlineSaveState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'saved'; readonly note: string | null }
  | { readonly kind: 'error'; readonly message: string; readonly field?: string };

export const INLINE_SAVE_IDLE: InlineSaveState = { kind: 'idle' };

export function inlineSaving(): InlineSaveState {
  return { kind: 'saving' };
}

/** The 2xx landing: whatever `describeDelivery` has to say, or nothing. */
export function inlineSaved(delivery: DeliveryState | undefined): InlineSaveState {
  return { kind: 'saved', note: describeDelivery(delivery) };
}

export function inlineSaveError(message: string, field?: string): InlineSaveState {
  return { kind: 'error', message, ...(field !== undefined ? { field } : {}) };
}

export function isSaving(state: InlineSaveState): boolean {
  return state.kind === 'saving';
}

/** The error text to surface, or `null` — one accessor, so the panel has one. */
export function saveErrorText(state: InlineSaveState): string | null {
  return state.kind === 'error' ? state.message : null;
}

/** Which field an error belongs to (custom saves map errors to their field). */
export function saveErrorField(state: InlineSaveState): string | null {
  return state.kind === 'error' ? (state.field ?? null) : null;
}

export const SAVE_LABEL = 'Save to boat';
export const SAVING_LABEL = 'Saving…';

export function saveButtonLabel(state: InlineSaveState): string {
  return isSaving(state) ? SAVING_LABEL : SAVE_LABEL;
}

/**
 * The owner's sentence for `422 recipe_needs_boat_update`, on a create (the
 * Add-alarm page) or an edit (the inline editor, AlarmDetail): one wording for
 * every surface. The version comes from the refusal's structured
 * `details.minimumCoreVersion`, never from its prose.
 */
export function boatUpdateNeededMessage(err: unknown): string {
  const details = (err as { body?: { details?: { minimumCoreVersion?: unknown } } } | null)?.body
    ?.details;
  const version =
    typeof details?.minimumCoreVersion === 'string' && details.minimumCoreVersion.length > 0
      ? details.minimumCoreVersion
      : undefined;
  return version === undefined
    ? 'Update the software on your boat to use this. Your boat’s version can’t run it yet.'
    : `Update the software on your boat to use this. It needs version ${version} or newer.`;
}

/**
 * A recipe PATCH's failure, worded. A 400 carries the API's OWN sentence when
 * it gives one — hiding a contract mismatch behind "check the values" sends an
 * operator hunting through numbers that were never the problem (the same rule
 * `AlarmDetail.saveEdit` follows).
 */
export function recipePatchErrorMessage(err: unknown): string {
  const e = err as { status?: number; body?: unknown } | null;
  const body = e?.body;
  const reason =
    body !== null && typeof body === 'object' && 'message' in body
      ? String((body as { message?: unknown }).message ?? '').trim()
      : '';
  if (e?.status === 400) {
    return reason.length > 0
      ? `That change couldn’t be saved — ${reason}`
      : 'That change couldn’t be saved — please check the values and try again.';
  }
  // The recipe is withdrawn, so no edit can save: the server's own sentence,
  // or the shared fallback when it sent none.
  if (isRecipeWithdrawnError(err)) return withdrawnErrorMessage(err);
  // The boat's software is too old to arm this alarm on a channel (TOM-136):
  // trying again changes nothing, an update does.
  if (e?.status === 422 && apiErrorCode(err) === 'recipe_needs_boat_update') {
    return boatUpdateNeededMessage(err);
  }
  return 'Something went wrong saving this alarm. Please try again.';
}

/**
 * The footer caption. Saving puts the change in the CLOUD; the boat is the one
 * that has to run it, and this line is where the panel says so before the save
 * rather than only after it. The standing boat-sync sentence rides along when
 * there is one — it is a stronger fact than the promise ("still running its own
 * settings" outranks "applied when it next connects").
 */
export function inlineSyncCaption(input: {
  readonly boatOnline: boolean;
  readonly boatSyncNote?: string | null;
}): string {
  const base = input.boatOnline
    ? 'Applied on your boat as soon as it’s saved.'
    : 'Applied when the boat next connects.';
  const note = input.boatSyncNote?.trim();
  return note !== undefined && note.length > 0 ? `${base} · ${note}` : base;
}

// ---------------------------------------------------------------------------
// Removing the alarm
// ---------------------------------------------------------------------------

/**
 * Where the delete is. It is a state machine rather than a boolean because the
 * confirm has to SURVIVE the failure: an error that dropped the panel back to
 * "Delete this alarm" would read as "it was removed" when nothing was.
 */
export type InlineDeleteState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'confirming' }
  | { readonly kind: 'deleting' }
  | { readonly kind: 'error'; readonly message: string };

export const INLINE_DELETE_IDLE: InlineDeleteState = { kind: 'idle' };

export function askToDelete(): InlineDeleteState {
  return { kind: 'confirming' };
}

export function cancelDelete(): InlineDeleteState {
  return INLINE_DELETE_IDLE;
}

export function deleting(): InlineDeleteState {
  return { kind: 'deleting' };
}

/** The failed DELETE. Says nothing about WHY — there is nothing to act on. */
export function deleteFailed(): InlineDeleteState {
  return { kind: 'error', message: DELETE_ERROR_MESSAGE };
}

/** True while the confirm is on screen — including after a failed attempt. */
export function isDeleteConfirming(state: InlineDeleteState): boolean {
  return state.kind !== 'idle';
}

export function isDeleting(state: InlineDeleteState): boolean {
  return state.kind === 'deleting';
}

export function deleteErrorText(state: InlineDeleteState): string | null {
  return state.kind === 'error' ? state.message : null;
}

/** The affordance that OPENS the confirm. Quiet, and never the first thing. */
export const DELETE_LABEL = 'Delete this alarm';

/**
 * The confirm's one sentence, shared with `AlarmDetail`. It states the
 * CONSEQUENCE rather than asking "are you sure", which tells an owner nothing
 * they did not already know — and only the consequence the cloud can keep before
 * it knows whether the boat is reachable (ADR 0213): the alarm goes off on the
 * boat first, and leaves the list only once the boat has taken that. What
 * actually happened is `describeRemoval`'s job, after the click.
 */
export const DELETE_PROMPT =
  'Turns this alarm off on your boat, then removes it. If your boat isn’t connected, the alarm stays in your list, turned off.';
export const DELETE_CONFIRM_LABEL = 'Remove alarm';
export const DELETING_LABEL = 'Removing…';
export const DELETE_KEEP_LABEL = 'Keep it';
export const DELETE_ERROR_MESSAGE = 'Something went wrong removing this alarm. Please try again.';

export function deleteButtonLabel(state: InlineDeleteState): string {
  return isDeleting(state) ? DELETING_LABEL : DELETE_CONFIRM_LABEL;
}

// ---------------------------------------------------------------------------
// The dirty edit: draft + the baseline it will be diffed against
// ---------------------------------------------------------------------------

/**
 * A dirty panel's whole state: the draft being typed, the DEFINITION it was
 * seeded from, and each condition row's original stored index.
 *
 * The baseline is the fix for a quiet data-loss bug. The panel stays mounted
 * across snapshot refetches, and while it is dirty the draft deliberately stops
 * following the refreshed definition — but the SAVE used to diff against the
 * freshest one. A field this owner never touched (say, a severity another tab
 * just changed) would then differ from the stale draft and ship in the patch,
 * silently reverting the newer save. Diffing against the seed definition means
 * untouched fields can never enter the patch; a field the owner DID edit is
 * last-write-wins, which is what pressing "Save to boat" claims.
 *
 * `conditionKeys[i]` is draft condition `i`'s index in the baseline definition.
 * Removing a middle row used to shift every later row onto its neighbour's
 * stored clause — wrong live proof, wrong in-force gate, remount-on-remove.
 * The keys only ever splice, so they survive as stable React keys too.
 */
export interface InlineEdit<D, Def> {
  readonly draft: D;
  readonly baseline: Def;
  readonly conditionKeys: readonly number[];
}

/**
 * Fold one edit into the state: the FIRST edit captures the definition it was
 * made against (on that render the draft is seeded from `currentDef`, so the
 * two are consistent by construction); later edits keep the captured one.
 */
export function applyInlineEdit<D, Def>(
  prev: InlineEdit<D, Def> | null,
  currentDef: Def,
  draft: D,
  conditionKeys: readonly number[]
): InlineEdit<D, Def> {
  return prev === null
    ? { draft, baseline: currentDef, conditionKeys }
    : { draft, baseline: prev.baseline, conditionKeys };
}

/** A fresh draft's keys: its rows sit at their own stored indices. */
export function initialConditionKeys(count: number): number[] {
  return Array.from({ length: count }, (_, i) => i);
}

/** Splice one key out; an out-of-range index changes nothing. */
export function conditionKeysAfterRemove(
  keys: readonly number[],
  index: number
): readonly number[] {
  if (index < 0 || index >= keys.length) return keys;
  return keys.filter((_, i) => i !== index);
}

// ---------------------------------------------------------------------------
// The unsaved-changes guard on the panel's continuation links
// ---------------------------------------------------------------------------

/**
 * The links out of a DIRTY panel — "Open full editor", "+ add condition" —
 * navigate to a page that cannot carry the half-typed draft along, so they get
 * an inline confirm instead of silently dropping it. Same shape as the delete
 * confirm: consequence first, then the two honest moves. No "save first"
 * action — the real Save button is in the same footer, one tab away.
 */
export const UNSAVED_GUARD_MESSAGE = 'You have unsaved changes here.';
export const UNSAVED_GUARD_DISCARD = 'Discard them and open the full editor';
export const UNSAVED_GUARD_STAY = 'Keep editing';

// ---------------------------------------------------------------------------
// The condition stack's joint
// ---------------------------------------------------------------------------

/**
 * The word on the joint pill between two clauses.
 *
 * The data model carries ONE logic for the whole clause set, so every joint
 * shows the same word and flipping any joint flips them all. The design draws
 * per-joint pills; mixed logic is not a thing this API can express, and
 * rendering two different words would promise something no save could keep.
 */
export function jointLabel(logic: 'all' | 'any'): 'AND' | 'OR' {
  return logic === 'any' ? 'OR' : 'AND';
}

/** The other logic — what clicking the joint pill selects. */
export function toggledLogic(logic: 'all' | 'any'): 'all' | 'any' {
  return logic === 'any' ? 'all' : 'any';
}

/**
 * The stack footer's WHOLE-ALARM pill (`combinedWouldFire`).
 *
 * Deliberately NOT the clause vocabulary. "true now" on a clause means that
 * clause holds; the same words under the stack would be read as "this alarm is
 * true", which is not a thing an alarm is. This says what the boat would do,
 * and keeps the three-way honesty: a definite no is quiet, a definite yes would
 * sound, and anything unreadable says it cannot tell rather than "quiet".
 */
export function combinedVerdict(verdict: boolean | null): {
  readonly text: string;
  readonly tone: 'firing' | 'quiet' | 'unknown';
  readonly detail: string;
} {
  if (verdict === true) {
    return {
      text: 'would sound now',
      tone: 'firing',
      detail: 'On the current readings the trigger is met and the conditions allow it.',
    };
  }
  if (verdict === false) {
    return {
      text: 'quiet now',
      tone: 'quiet',
      detail: 'On the current readings this alarm would stay quiet.',
    };
  }
  return {
    text: 'can’t tell',
    tone: 'unknown',
    detail: 'Something here has no recent reading, so what this alarm would do can’t be said.',
  };
}

/** The caption under the stack, naming what the whole set currently means. */
export function jointExplanation(logic: 'all' | 'any'): string {
  return logic === 'any'
    ? 'This alarm is allowed to sound while ANY of these is true.'
    : 'This alarm is allowed to sound only while ALL of these are true.';
}
